/**
 * Panel controller — owns session, bootstrap, bridge, and chat stream.
 * App.tsx stays a thin view over this hook (Flutter ViewModel-style).
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { changeLocale, i18n } from '../i18n';
import { log } from '../config/env';
import {
  postToHost,
  readQueryParams,
  resolvePublishableKey,
} from '../lib/bridge';
import { isHostBridgeMessage } from '../lib/apiGuards';
import {
  useAttachments,
  type AttachmentNotice,
  type UploadFn,
} from './useAttachments';
import { uploadToStorage } from '../lib/attachments';
import {
  isIdentityVerificationEnabled,
  isLauncherEnabled,
  resolveFeatures,
  resolvePopularQuestions,
} from '../lib/configHelpers';
import {
  WidgetApiError,
  describeError,
  isAbortError,
  isNetworkFailure,
  isWidgetDisabledError,
} from '../lib/errors';
import { shouldAutoOpen, isTrustedHostMessage } from '../lib/hostTrust';
import { createId } from '../lib/ids';
import { streamAvatarReply } from '../lib/sseClient';
import { isRecord } from '../lib/typeGuards';
import { readVisitorId, writeVisitorId } from '../lib/visitorStorage';
import {
  ensureSession,
  fetchWidgetConfig,
  isSessionValid,
  presignAttachment,
  type ActiveSession,
} from '../lib/widgetApi';
import {
  applyConfigAccent,
  applyConfigFont,
  clearConfigAccent,
  resolveConfigLocale,
} from '../theme';
import type { WidgetConfig, WidgetFeatures } from '../types/api';
import type { WidgetChannel } from '../types/bridge';
import type {
  ChatMessage,
  ComposerAttachment,
  SentAttachment,
} from '../types/chat';

/** Send-time attachment refusals: retrying the same paths can't succeed. */
const ATTACHMENT_SEND_ERRORS = {
  invalid_attachment: 'attachment.sendInvalid',
  file_type_not_allowed: 'attachment.sendTypeNotAllowed',
  file_too_large: 'attachment.sendTooLarge',
  file_upload_disabled: 'attachment.disabled',
} as const;

function attachmentSendErrorKey(error?: unknown) {
  if (!(error instanceof WidgetApiError)) return null;
  return (
    ATTACHMENT_SEND_ERRORS[error.code as keyof typeof ATTACHMENT_SEND_ERRORS] ??
    null
  );
}

/** Session refusals shown in plain language (AW-12 A7), not the generic fallback. */
const SESSION_ERRORS = {
  origin_not_allowed: 'chat.originNotAllowlisted',
  bundle_not_allowed: 'chat.bundleNotAllowlisted',
  identity_required: 'chat.identifyRequired',
  identity_invalid: 'chat.identifyRequired',
  identity_expired: 'chat.identifyRequired',
  rate_limited: 'chat.rateLimited',
} as const;

function publicErrorKey(error?: unknown, reason?: string) {
  if (isWidgetDisabledError(error, reason)) return 'chat.widgetDisabled';
  const attachmentKey = attachmentSendErrorKey(error);
  if (attachmentKey) return attachmentKey;
  if (error instanceof WidgetApiError && Object.hasOwn(SESSION_ERRORS, error.code)) {
    return SESSION_ERRORS[error.code as keyof typeof SESSION_ERRORS];
  }
  if (
    error instanceof WidgetApiError &&
    error.code === 'attachment_unavailable'
  ) {
    return 'attachment.unavailable';
  }
  if (isNetworkFailure(error)) return 'chat.networkError';
  return 'chat.errorFallback';
}

/** The "couldn't confirm your sign-in" system message, cleared once identify succeeds. */
function isSignInError(m: ChatMessage) {
  return (
    m.sender === 'system' &&
    m.status === 'error' &&
    m.text === i18n.t('chat.identifyRequired')
  );
}

/** Object URLs of images shown in sent bubbles — revoked when the thread is cleared. */
function revokeSentPreviews(messages: ChatMessage[]) {
  messages.forEach((m) =>
    m.attachments?.forEach((a) => {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    }),
  );
}

export interface AvatarWidgetController {
  channel: WidgetChannel;
  isOpen: boolean;
  /** Config loaded or bootstrap failed — ready to show chat (not shimmer). */
  isPanelReady: boolean;
  messages: ChatMessage[];
  isStreaming: boolean;
  bootstrapping: boolean;
  serviceUnavailable: boolean;
  lastFailedText: string | null;
  widgetConfig: WidgetConfig | null;
  greetingText: string;
  features: WidgetFeatures;
  popularQuestions: string[];
  showPopular: boolean;
  inputDisabled: boolean;
  sendMessage: (text: string) => Promise<void>;
  /** Resends the last failed message with the same uploaded files. */
  retryLastMessage: () => void;
  /** Composer files (max MAX_ATTACHMENTS). Purged with the chat on close. */
  attachments: ComposerAttachment[];
  attachmentNotice: AttachmentNotice | null;
  addAttachments: (files: File[]) => void;
  removeAttachment: (id: string) => void;
  retryAttachment: (id: string) => void;
  resetConversation: () => Promise<void>;
  /** Mobile chrome back — purges in-memory chat and asks the native host to dismiss. */
  dismissPanel: () => void;
}

export function useAvatarWidget(): AvatarWidgetController {
  const { t } = useTranslation();
  const { publishableKey, channel, bundleId, requestedLocale } = readQueryParams();
  const autoOpen = shouldAutoOpen();

  const [isOpen, setIsOpen] = useState(autoOpen);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [lastFailedText, setLastFailedText] = useState<string | null>(null);
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig | null>(null);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [isConfigReady, setIsConfigReady] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const bootstrapAbortRef = useRef<AbortController | null>(null);
  const configLoadRef = useRef<Promise<WidgetConfig> | null>(null);
  const pkRef = useRef(publishableKey);
  const channelRef = useRef<WidgetChannel>(channel);
  const bundleRef = useRef(bundleId);
  const sessionRef = useRef<ActiveSession | null>(null);
  const configRef = useRef<WidgetConfig | null>(null);
  const identityRef = useRef<{ userId: string; hmac: string } | null>(null);
  const conversationStartedRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  const messagesRef = useRef<ChatMessage[]>([]);
  /** Set when Gateway says the widget was disabled mid-session. */
  const widgetDisabledRef = useRef(false);
  /** Files of the last failed send, reused by retry (the platform keeps the uploads). */
  const lastFailedAttachmentsRef = useRef<SentAttachment[]>([]);
  /** One session mint shared by parallel uploads, so 5 files don't create 5 sessions. */
  const sessionInFlightRef = useRef<Promise<ActiveSession> | null>(null);

  const uploadAttachment = useCallback<UploadFn>(
    async (file, extension, onProgress, signal) => {
      sessionInFlightRef.current ??= ensureSession({
        publishableKey: pkRef.current,
        channel: channelRef.current,
        bundleId: bundleRef.current,
        visitorId:
          sessionRef.current?.visitorId || readVisitorId(pkRef.current),
        current: sessionRef.current,
        identity: identityRef.current,
      }).finally(() => {
        sessionInFlightRef.current = null;
      });
      const session = await sessionInFlightRef.current;
      sessionRef.current = session;

      const presign = await presignAttachment({
        accessToken: session.accessToken,
        channel: channelRef.current,
        bundleId: bundleRef.current,
        extension,
        size: file.size,
        signal,
      });
      await uploadToStorage(presign, file, onProgress, signal);
      return { path: presign.path, sessionId: session.sessionId };
    },
    [],
  );

  const {
    attachments,
    notice: attachmentNotice,
    addAttachments,
    reportTooLarge,
    removeAttachment,
    retryAttachment,
    takeAttachments,
    clearAttachments,
  } = useAttachments(uploadAttachment);
  // Bridge listener is registered once; reach the latest callbacks through refs.
  const addAttachmentsRef = useRef(addAttachments);
  const reportTooLargeRef = useRef(reportTooLarge);
  useEffect(() => {
    addAttachmentsRef.current = addAttachments;
    reportTooLargeRef.current = reportTooLarge;
  }, [addAttachments, reportTooLarge]);

  const greetingText = widgetConfig?.welcome_message?.trim() || '';
  const features = resolveFeatures(widgetConfig?.features);
  const popularQuestions = resolvePopularQuestions(
    widgetConfig?.popular_questions,
  );

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    configRef.current = widgetConfig;
  }, [widgetConfig]);

  const purgeConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    bootstrapAbortRef.current?.abort();
    bootstrapAbortRef.current = null;
    configLoadRef.current = null;
    // Session JWT is in-memory only. visitor_id stays in localStorage (§8.2).
    sessionRef.current = null;
    identityRef.current = null;
    configRef.current = null;
    conversationStartedRef.current = false;
    widgetDisabledRef.current = false;
    clearConfigAccent();
    applyConfigFont(undefined);
    setWidgetConfig(null);
    setIsConfigReady(false);
    revokeSentPreviews(messagesRef.current);
    lastFailedAttachmentsRef.current = [];
    setMessages([]);
    setIsStreaming(false);
    setServiceUnavailable(false);
    setLastFailedText(null);
    setBootstrapping(false);
    clearAttachments();
  }, [clearAttachments]);

  const dismissPanel = useCallback(() => {
    purgeConversation();
    setIsOpen(false);
    postToHost('WIDGET_CLOSE');
  }, [purgeConversation]);

  const startFreshConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    conversationStartedRef.current = false;
    revokeSentPreviews(messagesRef.current);
    lastFailedAttachmentsRef.current = [];
    // Uploaded paths only work in the old session's chat.
    clearAttachments();
    setMessages([]);
    setIsStreaming(false);
    setServiceUnavailable(false);
    setLastFailedText(null);
    // Open clears the launcher unread badge (proposal §5.2).
    postToHost('UNREAD_CHANGED', { unreadCount: 0 });
  }, [clearAttachments]);

  /** Clear the thread and mint a new session JWT (same visitor_id). */
  const resetConversation = useCallback(async () => {
    if (isStreaming || bootstrapping || !pkRef.current) return;

    startFreshConversation();

    const visitorId =
      sessionRef.current?.visitorId || readVisitorId(pkRef.current);
    const controller = new AbortController();
    abortRef.current = controller;
    setBootstrapping(true);

    try {
      const identityRequired = isIdentityVerificationEnabled(
        configRef.current?.identity_verification,
      );
      if (identityRequired && !identityRef.current) {
        setMessages([
          {
            id: createId('system'),
            text: t('chat.identifyRequired'),
            sender: 'system',
            status: 'error',
            createdAt: Date.now(),
          },
        ]);
        return;
      }

      const session = await ensureSession({
        publishableKey: pkRef.current,
        channel: channelRef.current,
        bundleId: bundleRef.current,
        visitorId,
        current: null,
        identity: identityRef.current,
        force: true,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      sessionRef.current = session;
      writeVisitorId(pkRef.current, session.visitorId);
      postToHost('WIDGET_THEME', {
        sessionId: session.sessionId,
        avatarId: session.avatarId,
      });
      log('info', 'Session refreshed after reset', {
        sessionId: session.sessionId,
        visitorId: session.visitorId,
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      const reason = describeError(error, t('chat.errorFallback'));
      const publicText = t(publicErrorKey(error, reason));
      log('error', 'Session refresh after reset failed', reason);
      if (isWidgetDisabledError(error, reason)) {
        widgetDisabledRef.current = true;
      }
      setServiceUnavailable(true);
      setMessages([
        {
          id: createId('system'),
          text: publicText,
          sender: 'system',
          status: 'error',
          createdAt: Date.now(),
        },
      ]);
      postToHost('API_STREAM_ERROR', { reason });
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setBootstrapping(false);
      }
    }
  }, [bootstrapping, isStreaming, startFreshConversation, t]);

  /** GET /v1/config — deduped; applies accent before UI is shown. */
  const ensureConfigLoaded = useCallback(
    async (pk: string, signal?: AbortSignal): Promise<WidgetConfig> => {
      if (configRef.current) {
        applyConfigAccent(configRef.current.theme.accent_color);
        applyConfigFont(configRef.current.theme.font_family);
        setIsConfigReady(true);
        return configRef.current;
      }

      if (configLoadRef.current) {
        return configLoadRef.current;
      }

      const load = (async () => {
        const config = await fetchWidgetConfig(pk, { signal });
        applyConfigAccent(config.theme.accent_color);
        applyConfigFont(config.theme.font_family);
        setWidgetConfig(config);
        setIsConfigReady(true);
        return config;
      })();

      configLoadRef.current = load;
      try {
        return await load;
      } finally {
        if (configLoadRef.current === load) {
          configLoadRef.current = null;
        }
      }
    },
    [],
  );

  /**
   * Load avatar config + open a chat session.
   * Privacy: chat + session JWT live in memory only; purged on WIDGET_CLOSE.
   * visitor_id is persisted per pk on the panel origin (proposal §8.2).
   */
  const bootstrapWidget = useCallback(async (pk: string) => {
    if (!pk) {
      log('warn', 'bootstrap skipped — missing publishable key');
      return;
    }

    bootstrapAbortRef.current?.abort();
    const controller = new AbortController();
    bootstrapAbortRef.current = controller;
    const isCurrent = () => bootstrapAbortRef.current === controller;
    setBootstrapping(true);

    try {
      const config = await ensureConfigLoaded(pk, controller.signal);
      if (!isCurrent()) return;

      const configLocale = resolveConfigLocale({
        requested: requestedLocale,
        browserLanguages: navigator.languages,
        localeDefault: config.locale_default,
        localesSupported: config.locales_supported,
      });
      if (configLocale) {
        await changeLocale(configLocale);
        if (!isCurrent()) return;
      }

      if (!isIdentityVerificationEnabled(config.identity_verification)) {
        identityRef.current = null;
      }

      const identityRequired = isIdentityVerificationEnabled(
        config.identity_verification,
      );
      if (identityRequired && !identityRef.current) {
        // Gateway rejects POST /v1/sessions without identity when enabled.
        // Wait for WIDGET_IDENTIFY (host calls identify before/after open).
        log('info', 'Identity verification enabled — waiting for identify()', {
          avatarId: config.avatar_id,
        });
        setServiceUnavailable(false);
        postToHost('WIDGET_THEME', {
          name: config.name,
          avatarId: config.avatar_id,
          accentColor: config.theme.accent_color,
          launcherIcon: config.theme.launcher_icon,
          position: config.theme.position,
          identityVerification: config.identity_verification,
          locale: i18n.language,
          features: {
            fileUpload: config.features.file_upload,
            voice: config.features.voice,
            preChatForm: config.features.pre_chat_form,
          },
        });
        return;
      }

      if (!isLauncherEnabled(config)) {
        postToHost('WIDGET_THEME', {
          name: config.name,
          avatarId: config.avatar_id,
          accentColor: config.theme.accent_color,
          launcherIcon: config.theme.launcher_icon,
          position: config.theme.position,
          identityVerification: config.identity_verification,
          widgetStatus: false,
          locale: i18n.language,
          features: {
            fileUpload: config.features.file_upload,
            voice: config.features.voice,
            preChatForm: config.features.pre_chat_form,
          },
        });
        setServiceUnavailable(false);
        log('info', 'Widget hidden — widget_status is false', {
          avatarId: config.avatar_id,
        });
        return;
      }

      const session = await ensureSession({
        publishableKey: pk,
        channel: channelRef.current,
        bundleId: bundleRef.current,
        visitorId: sessionRef.current?.visitorId || readVisitorId(pk),
        current: sessionRef.current,
        identity: identityRef.current,
        signal: controller.signal,
      });
      if (!isCurrent()) return;

      sessionRef.current = session;
      writeVisitorId(pk, session.visitorId);
      postToHost('WIDGET_THEME', {
        name: config.name,
        avatarId: config.avatar_id,
        accentColor: config.theme.accent_color,
        launcherIcon: config.theme.launcher_icon,
        position: config.theme.position,
        identityVerification: config.identity_verification,
        widgetStatus: config.widget_status,
        sessionId: session.sessionId,
        locale: i18n.language,
        features: {
          fileUpload: config.features.file_upload,
          voice: config.features.voice,
          preChatForm: config.features.pre_chat_form,
        },
      });
      setServiceUnavailable(false);
      log('info', 'Widget bootstrapped', {
        avatarId: config.avatar_id,
        sessionId: session.sessionId,
        visitorId: session.visitorId,
        accent: config.theme.accent_color,
        locale: i18n.language,
        localesSupported: config.locales_supported,
        identityVerification: config.identity_verification,
        widgetStatus: config.widget_status,
        hasIdentity: Boolean(identityRef.current),
        features: config.features,
        branding: config.branding,
      });
    } catch (error) {
      if (!isCurrent()) return;
      if (controller.signal.aborted || isAbortError(error)) return;

      const reason = describeError(error, i18n.t('chat.errorFallback'));
      const publicText = i18n.t(publicErrorKey(error, reason));
      log('error', 'Widget bootstrap failed', reason);
      if (isWidgetDisabledError(error, reason)) {
        widgetDisabledRef.current = true;
      }
      setServiceUnavailable(true);
      setMessages([
        {
          id: createId('system'),
          text: publicText,
          sender: 'system',
          status: 'error',
          createdAt: Date.now(),
        },
      ]);
      postToHost('API_STREAM_ERROR', { reason });
    } finally {
      if (isCurrent()) setBootstrapping(false);
    }
  }, [ensureConfigLoaded, requestedLocale]);

  /** Prefetch config on load — no AbortSignal (StrictMode cleanup must not cancel shared fetch). */
  useEffect(() => {
    if (!publishableKey) return;

    void ensureConfigLoaded(publishableKey).catch((error) => {
      if (isAbortError(error)) return;
      log('debug', 'Config prefetch failed (will retry on open)', error);
    });
  }, [publishableKey, ensureConfigLoaded]);

  useEffect(() => {
    if (!autoOpen || !pkRef.current) return;
    void bootstrapWidget(pkRef.current);
  }, [autoOpen, bootstrapWidget]);

  useEffect(() => {
    const handleBridgeMessages = (event: MessageEvent) => {
      if (!isHostBridgeMessage(event.data)) return;
      if (!isTrustedHostMessage(event.origin)) return;

      if (event.data.event === 'WIDGET_OPEN') {
        const payload = isRecord(event.data.payload) ? event.data.payload : {};
        const key = resolvePublishableKey({
          publishableKey:
            typeof payload.publishableKey === 'string'
              ? payload.publishableKey
              : undefined,
          publicKey:
            typeof payload.publicKey === 'string'
              ? payload.publicKey
              : undefined,
        });
        if (key) pkRef.current = key;
        setIsOpen(true);
        startFreshConversation();
        void bootstrapWidget(pkRef.current);
      }

      if (event.data.event === 'WIDGET_CLOSE') {
        if (widgetDisabledRef.current) {
          postToHost('WIDGET_THEME', { widgetStatus: false });
        }
        setIsOpen(false);
        // PRIVACY: purge chat + JWT. visitor_id remains in localStorage (§8.2).
        purgeConversation();
      }

      // iOS SDK native picker (no camera) — one file per message, base64.
      if (event.data.event === 'FILE_PICKER_RESULT') {
        const payload = isRecord(event.data.payload) ? event.data.payload : {};
        const file = isRecord(payload.file) ? payload.file : null;
        if (file && file.tooLarge === true && typeof file.name === 'string') {
          reportTooLargeRef.current(file.name);
        } else if (
          file &&
          typeof file.name === 'string' &&
          typeof file.type === 'string' &&
          typeof file.data === 'string'
        ) {
          const bytes = Uint8Array.from(atob(file.data), (c) =>
            c.charCodeAt(0),
          );
          void addAttachmentsRef.current([
            new File([bytes], file.name, { type: file.type }),
          ]);
        }
      }

      if (event.data.event === 'WIDGET_IDENTIFY') {
        const payload = isRecord(event.data.payload) ? event.data.payload : {};
        const userId =
          typeof payload.userId === 'string'
            ? payload.userId
            : typeof payload.user_id === 'string'
              ? payload.user_id
              : undefined;
        const hmac =
          typeof payload.hmac === 'string'
            ? payload.hmac
            : typeof payload.userHash === 'string'
              ? payload.userHash
              : typeof payload.user_hash === 'string'
                ? payload.user_hash
                : undefined;
        if (!userId || !hmac) return;

        const verification = configRef.current?.identity_verification;
        if (
          verification !== undefined &&
          !isIdentityVerificationEnabled(verification)
        ) {
          log('warn', i18n.t('chat.identifyDisabled'));
          return;
        }
        identityRef.current = { userId, hmac };
        log('info', 'Identity payload stored — minting session with HMAC', {
          userId,
        });

        // Bind mid-session or complete bootstrap that waited for identify.
        if (pkRef.current) {
          void (async () => {
            try {
              const session = await ensureSession({
                publishableKey: pkRef.current,
                channel: channelRef.current,
                bundleId: bundleRef.current,
                visitorId:
                  sessionRef.current?.visitorId ||
                  readVisitorId(pkRef.current),
                current: sessionRef.current,
                identity: identityRef.current,
                force: true,
              });
              sessionRef.current = session;
              writeVisitorId(pkRef.current, session.visitorId);
              setServiceUnavailable(false);
              setMessages((prev) => prev.filter((m) => !isSignInError(m)));
              postToHost('WIDGET_THEME', {
                sessionId: session.sessionId,
                identityVerification:
                  configRef.current?.identity_verification,
              });
            } catch (error) {
              const reason = describeError(
                error,
                i18n.t('chat.errorFallback'),
              );
              log('error', 'Session after identify failed', reason);
              if (isWidgetDisabledError(error, reason)) {
                widgetDisabledRef.current = true;
              }
              setServiceUnavailable(true);
              // Keep the transcript; replace an earlier sign-in error instead of stacking it.
              setMessages((prev) => [
                ...prev.filter((m) => !isSignInError(m)),
                {
                  id: createId('system'),
                  text: i18n.t(publicErrorKey(error, reason)),
                  sender: 'system',
                  status: 'error',
                  createdAt: Date.now(),
                },
              ]);
              postToHost('API_STREAM_ERROR', { reason });
            }
          })();
        }
      }
    };

    window.addEventListener('message', handleBridgeMessages);
    postToHost('WIDGET_READY');

    return () => {
      window.removeEventListener('message', handleBridgeMessages);
      abortRef.current?.abort();
    };
  }, [bootstrapWidget, purgeConversation, startFreshConversation]);

  const sendMessage = useCallback(
    async (text: string, retryAttachments?: SentAttachment[]) => {
      if (serviceUnavailable || isStreaming || bootstrapping) return;

      let sent = retryAttachments;
      if (!sent) {
        // Composer only enables send once every file has uploaded.
        if (attachments.some((a) => a.status !== 'done')) return;
        sent = takeAttachments().map((a) => ({
          kind: a.kind,
          name: a.file.name,
          size: a.file.size,
          previewUrl: a.previewUrl,
          path: a.path ?? '',
          sessionId: a.sessionId,
        }));
      }
      const sentAttachments = sent;
      if (!text.trim() && sentAttachments.length === 0) return;

      const sentAt = Date.now();
      const userMsg: ChatMessage = {
        id: createId('user'),
        text,
        sender: 'user',
        status: 'complete',
        createdAt: sentAt,
        ...(sentAttachments.length ? { attachments: sentAttachments } : {}),
      };
      const streamId = createId('avatar');
      const placeholder: ChatMessage = {
        id: streamId,
        text: '',
        sender: 'avatar',
        status: 'streaming',
        createdAt: sentAt,
      };

      const previousSession = sessionRef.current;
      const sessionHadEnded = Boolean(
        previousSession && !isSessionValid(previousSession),
      );
      const hadTranscript = messagesRef.current.some(
        (m) => m.sender === 'user' || m.sender === 'avatar',
      );

      setMessages((prev) => [...prev, userMsg, placeholder]);
      setIsStreaming(true);
      setLastFailedText(null);

      const controller = new AbortController();
      abortRef.current = controller;

      const failStream = (
        reason: string,
        error?: unknown,
        publicKey?: 'attachment.sessionExpired' | 'chat.identifyRequired',
      ) => {
        const disabled = isWidgetDisabledError(error, reason);
        // Refused or expired files can't be resent as-is — no retry button.
        const retryable =
          !disabled && !publicKey && !attachmentSendErrorKey(error);
        const publicText = t(publicKey ?? publicErrorKey(error, reason));
        setMessages((prev) =>
          prev.map((m) =>
            m.id === streamId
              ? { ...m, sender: 'system', status: 'error', text: publicText }
              : m,
          ),
        );
        setLastFailedText(retryable ? text : null);
        lastFailedAttachmentsRef.current = retryable ? sentAttachments : [];
        setIsStreaming(false);
        if (disabled) {
          widgetDisabledRef.current = true;
          setServiceUnavailable(true);
        }
        postToHost('API_STREAM_ERROR', { reason });
      };

      try {
        const identityRequired = isIdentityVerificationEnabled(
          configRef.current?.identity_verification,
        );
        if (identityRequired && !identityRef.current) {
          failStream('identity_required', undefined, 'chat.identifyRequired');
          return;
        }

        const session = await ensureSession({
          publishableKey: pkRef.current,
          channel: channelRef.current,
          bundleId: bundleRef.current,
          visitorId:
            sessionRef.current?.visitorId || readVisitorId(pkRef.current),
          current: sessionRef.current,
          identity: identityRef.current,
          signal: controller.signal,
        });
        sessionRef.current = session;
        writeVisitorId(pkRef.current, session.visitorId);

        // A new session means a new upstream chat; files uploaded under the old one are refused.
        if (sentAttachments.some((a) => a.sessionId !== session.sessionId)) {
          failStream(
            'Attachments belong to an expired session',
            undefined,
            'attachment.sessionExpired',
          );
          return;
        }

        if (hadTranscript && sessionHadEnded) {
          conversationStartedRef.current = false;
          setMessages((prev) => {
            const insertAt = prev.findIndex((m) => m.id === userMsg.id);
            if (insertAt < 0) return prev;
            if (prev[insertAt - 1]?.sender === 'divider') return prev;
            const divider: ChatMessage = {
              id: createId('divider'),
              text: t('chat.newConversationStarted'),
              sender: 'divider',
              createdAt: Date.now(),
            };
            return [
              ...prev.slice(0, insertAt),
              divider,
              ...prev.slice(insertAt),
            ];
          });
        }

        let activeConversationId: string | undefined;

        await streamAvatarReply({
          accessToken: session.accessToken,
          message: text,
          attachments: sentAttachments.map((a) => ({
            path: a.path,
            file_name: a.name,
          })),
          channel: channelRef.current,
          bundleId: bundleRef.current,
          signal: controller.signal,
          onChunk: (chunk) => {
            if (chunk.type === 'started') {
              if (chunk.conversationId) {
                activeConversationId = chunk.conversationId;
              }
              if (activeConversationId && !conversationStartedRef.current) {
                conversationStartedRef.current = true;
                postToHost('CONVERSATION_STARTED', {
                  conversationId: activeConversationId,
                  sessionId: chunk.sessionId || session.sessionId,
                  avatarId: chunk.avatarId || session.avatarId,
                  publishableKey: pkRef.current,
                });
              }
            }

            if (chunk.type === 'token' && chunk.text) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === streamId ? { ...m, text: m.text + chunk.text } : m,
                ),
              );
            }

            if (chunk.type === 'done') {
              const completedText = chunk.text;
              setMessages((prev) => {
                const next = prev.map((m) =>
                  m.id === streamId
                    ? {
                        ...m,
                        text: m.text || completedText || '',
                        status: 'complete' as const,
                      }
                    : m,
                );
                const finalText =
                  next.find((m) => m.id === streamId)?.text || '';
                postToHost('MESSAGE_RECEIVED', {
                  conversationId: chunk.conversationId || activeConversationId,
                  sessionId: chunk.sessionId || session.sessionId,
                  avatarId: chunk.avatarId || session.avatarId,
                  publishableKey: pkRef.current,
                  role: 'assistant',
                  messagePreview: finalText.slice(0, 160),
                });
                if (!isOpenRef.current) {
                  postToHost('UNREAD_CHANGED', { unreadCount: 1 });
                }
                return next;
              });
              setIsStreaming(false);
            }

            if (chunk.type === 'error') {
              failStream(chunk.reason || t('chat.errorFallback'));
            }
          },
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        failStream(describeError(error, t('chat.errorFallback')), error);
      }
    },
    [
      attachments,
      bootstrapping,
      isStreaming,
      serviceUnavailable,
      t,
      takeAttachments,
    ],
  );

  const retryLastMessage = useCallback(() => {
    if (lastFailedText === null) return;
    void sendMessage(lastFailedText, lastFailedAttachmentsRef.current);
  }, [lastFailedText, sendMessage]);

  const showPopular =
    messages.length === 0 &&
    !isStreaming &&
    !serviceUnavailable &&
    (greetingText.length > 0 || popularQuestions.length > 0);
  const inputDisabled = serviceUnavailable || isStreaming || bootstrapping;
  const isPanelReady = isConfigReady || serviceUnavailable;

  return {
    channel,
    isOpen,
    isPanelReady,
    messages,
    isStreaming,
    bootstrapping,
    serviceUnavailable,
    lastFailedText,
    widgetConfig,
    greetingText,
    features,
    popularQuestions,
    showPopular,
    inputDisabled,
    sendMessage,
    retryLastMessage,
    attachments,
    attachmentNotice,
    addAttachments,
    removeAttachment,
    retryAttachment,
    resetConversation,
    dismissPanel,
  };
}
